import { forwardRef, useCallback } from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useTextDirection } from "@/tiptap/ui/text-direction-button"

import { Button } from "@/tiptap/ui-primitive/button"

export const TextDirectionButton = forwardRef((
  {
    editor: providedEditor,
    direction,
    text,
    hideWhenUnavailable = false,
    onDirected,
    onClick,
    icon: CustomIcon,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    handleTextDirection,
    label,
    canSetDirection,
    isActive,
    Icon,
  } = useTextDirection({
    editor,
    direction,
    hideWhenUnavailable,
    onDirected,
  })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleTextDirection()
  }, [handleTextDirection, onClick])

  if (!isVisible) {
    return null
  }

  const RenderIcon = CustomIcon ?? Icon

  return (
    <Button
      type="button"
      disabled={!canSetDirection}
      variant="ghost"
      data-active-state={isActive ? "on" : "off"}
      data-disabled={!canSetDirection}
      role="button"
      tabIndex={-1}
      aria-label={label}
      aria-pressed={isActive}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <RenderIcon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
        </>
      )}
    </Button>
  );
})

TextDirectionButton.displayName = "TextDirectionButton"
